import type { FreightBookingFormState } from '../components/freight-booking-wizard/freight-booking-wizard-types';
import type { LtlDefaults, LtlPickupServices } from '../types/ship-from';

/**
 * Converts a wire-format time string (`"HH:mm:ss"`) to the wizard's input
 * format (`"HH:mm"`). Returns empty string for null/empty input.
 */
export const toWizardTime = (wire: string | null | undefined): string => wire?.slice(0, 5) ?? '';

/**
 * Converts the wizard's input format (`"HH:mm"`) to wire format (`"HH:mm:ss"`).
 * Returns null for empty input.
 */
export const toWireTime = (wizard: string): string | null => (wizard ? `${wizard}:00` : null);

// ---------------------------------------------------------------------------
// AC6 normalizer — single choke point for the SPD-27629 invariant
// ---------------------------------------------------------------------------

/**
 * Enforces the mutual exclusivity of `willArrangePickupSeparately` and a
 * populated pickup window (AC6 / SPD-27629).
 *
 * Rule: flag wins.
 * - `willArrangePickupSeparately === true` → both times cleared.
 *
 * Applied at the end of every write path (prefill, checkbox toggle, save-back).
 */
export const normalizeLtlPickupWindow = <
  T extends { pickupReadyTime: string; pickupCloseTime: string; willArrangePickupSeparately: boolean },
>(
  x: T,
): T => {
  if (x.willArrangePickupSeparately) {
    return { ...x, pickupReadyTime: '', pickupCloseTime: '' };
  }
  return x;
};

const EMPTY_LTL_PICKUP_SERVICES: LtlPickupServices = Object.freeze({
  isInsidePickup: false,
  isConstructionSitePickup: false,
  isLiftgatePickup: false,
  isNotifyBeforePickup: false,
  isResidentialPickup: false,
  isDropAtTerminal: false,
  isGroceryConsolidationPickup: false,
  isLimitedAccess: false,
  isTradeshowPickup: false,
});

/** Frozen singleton — safe to use as a default prop or initial state. */
export const EMPTY_LTL_DEFAULTS: LtlDefaults = Object.freeze({
  pickupReadyTime: null,
  pickupCloseTime: null,
  willArrangePickupSeparately: false,
  pickupInstructions: null,
  pickupServices: EMPTY_LTL_PICKUP_SERVICES,
});

/**
 * Applies LTL defaults from a ship-from location onto the form state when the
 * user selects a location in the Booking Wizard.
 *
 * Contract:
 * 1. Guard: if `nextId === formState.selectedShipFromId` → return state unchanged.
 * 2. `ltlDefaults === null` → change **only** `selectedShipFromId`; leave all
 *    pickup fields as typed by the user (AC5 — "no defaults = behaves as today").
 * 3. `ltlDefaults !== null` → overwrite times, flag, and services.
 * 4. `pickupInstructions`: carry existing value if the defaults have no instructions
 *    (instructions render two steps away; a hard overwrite would destroy text
 *    the user typed with no visual feedback).
 * 5. Apply `normalizeLtlPickupWindow` last (AC6 invariant).
 *
 * Never touches `shipmentDate`, `destinationAddress`, or `handlingUnits`.
 * Never use a useEffect for prefill — event-driven only.
 */
export const applyLtlDefaultsToFormState = (
  formState: FreightBookingFormState,
  nextId: string,
  ltlDefaults: LtlDefaults | null | undefined,
): FreightBookingFormState => {
  if (nextId === formState.selectedShipFromId) {
    return formState;
  }

  const withId: FreightBookingFormState = {
    ...formState,
    selectedShipFromId: nextId,
    shouldSaveLtlDefaults: false,
    // Always reset pickup fields on location change — inheriting the previous
    // location's times/services when the new one has no defaults would mislead
    // the user into thinking they are already configured for this location.
    pickupReadyTime: '',
    pickupCloseTime: '',
    willArrangePickupSeparately: false,
    pickupServices: { ...EMPTY_LTL_PICKUP_SERVICES },
    pickupInstructions: '',
  };

  if (!ltlDefaults) {
    return withId;
  }

  const next: FreightBookingFormState = {
    ...withId,
    pickupReadyTime: toWizardTime(ltlDefaults.pickupReadyTime),
    pickupCloseTime: toWizardTime(ltlDefaults.pickupCloseTime),
    willArrangePickupSeparately: ltlDefaults.willArrangePickupSeparately,
    pickupServices: { ...ltlDefaults.pickupServices },
    // Deliberate asymmetry: instructions render in Additional Details (two steps away).
    // Prefer the user's in-progress text if the default has nothing.
    pickupInstructions: ltlDefaults.pickupInstructions ?? formState.pickupInstructions,
  };

  return normalizeLtlPickupWindow(next);
};

/**
 * Builds the save-back payload from the current wizard form state.
 *
 * Scoped to times + flag + services — pickup instructions are intentionally
 * excluded so a save-back never overwrites instructions stored in settings.
 *
 * The `existing` param carries `pickupInstructions` through so the endpoint
 * receives a complete `LtlDefaults` body without wiping stored instructions.
 */
export const buildLtlDefaultsFromFormState = (
  formState: FreightBookingFormState,
  existing: LtlDefaults | null | undefined,
): LtlDefaults => {
  const normalized = normalizeLtlPickupWindow({
    pickupReadyTime: formState.pickupReadyTime,
    pickupCloseTime: formState.pickupCloseTime,
    willArrangePickupSeparately: formState.willArrangePickupSeparately,
  });

  return {
    pickupReadyTime: toWireTime(normalized.pickupReadyTime),
    pickupCloseTime: toWireTime(normalized.pickupCloseTime),
    willArrangePickupSeparately: normalized.willArrangePickupSeparately,
    pickupServices: { ...formState.pickupServices },
    pickupInstructions: existing?.pickupInstructions ?? null,
  };
};

// ---------------------------------------------------------------------------
// Initial hydration (wizard opens with pre-selected ship-from)
// ---------------------------------------------------------------------------

/**
 * Like {@link applyLtlDefaultsToFormState} but **without** the same-id guard.
 *
 * Use this for one-time initial hydration when the wizard opens with a
 * ship-from already selected (e.g. from a default location) — `handleShipFromChange`
 * never fires in that case, so the guard in `applyLtlDefaultsToFormState` would
 * block the prefill. Safe to call in a one-time `useEffect`.
 *
 * Does NOT touch `shipmentDate`, `destinationAddress`, or `handlingUnits`.
 */
export const hydrateLtlDefaultsIntoFormState = (
  formState: FreightBookingFormState,
  ltlDefaults: LtlDefaults | null | undefined,
): FreightBookingFormState => {
  if (!ltlDefaults) return formState;

  const next: FreightBookingFormState = {
    ...formState,
    pickupReadyTime: toWizardTime(ltlDefaults.pickupReadyTime),
    pickupCloseTime: toWizardTime(ltlDefaults.pickupCloseTime),
    willArrangePickupSeparately: ltlDefaults.willArrangePickupSeparately,
    pickupServices: { ...ltlDefaults.pickupServices },
    pickupInstructions: ltlDefaults.pickupInstructions ?? formState.pickupInstructions,
  };

  return normalizeLtlPickupWindow(next);
};

/**
 * Like {@link hydrateLtlDefaultsIntoFormState} but fills only those pickup
 * fields the saved plan left blank. Use this when opening `LOAD_SAVED_QUOTE`
 * mode so that values the seller already entered are never overwritten.
 *
 * Per-field "blank" rules:
 * - Times: empty string (the `freight-payload-mappers` fallback when the draft
 *   has no readyTime/closeTime).
 * - `willArrangePickupSeparately`: preserved when `hasCompleteLtlPickupWindow`
 *   is true — i.e. when the draft has times OR the flag itself is true (which
 *   normalizeLtlPickupWindow always converts to empty times, so checking only
 *   `pickupReadyTime` would wrongly treat flag-only drafts as unconfigured).
 *   Filled from defaults only when the draft has neither times nor the flag set.
 * - `pickupServices`: filled from defaults whenever all service flags are false
 *   (the all-false state is indistinguishable from "never configured"). Checked
 *   independently of pickup times so a draft that has times but no services
 *   still gets defaults applied (SPD-28156 edge case).
 * - `pickupInstructions`: filled from defaults when blank (empty string or null).
 */
export const hydrateGapFillLtlDefaultsIntoFormState = (
  formState: FreightBookingFormState,
  ltlDefaults: LtlDefaults | null | undefined,
): FreightBookingFormState => {
  if (!ltlDefaults) return formState;

  // `willArrangePickupSeparately = true` is a complete pickup configuration on its own —
  // normalizeLtlPickupWindow always clears both time strings when it is set, so checking
  // only pickupReadyTime would incorrectly treat those drafts as "unconfigured" and
  // overwrite the seller's explicit flag with the location default.
  const hasExistingPickupWindow = hasCompleteLtlPickupWindow(formState);
  const hasAnyPickupService = Object.values(formState.pickupServices).some(Boolean);

  const next: FreightBookingFormState = {
    ...formState,
    pickupReadyTime: formState.pickupReadyTime || toWizardTime(ltlDefaults.pickupReadyTime),
    pickupCloseTime: formState.pickupCloseTime || toWizardTime(ltlDefaults.pickupCloseTime),
    willArrangePickupSeparately: hasExistingPickupWindow ? formState.willArrangePickupSeparately : ltlDefaults.willArrangePickupSeparately,
    pickupServices: hasAnyPickupService ? { ...formState.pickupServices } : { ...ltlDefaults.pickupServices },
    pickupInstructions: formState.pickupInstructions || ltlDefaults.pickupInstructions || '',
  };

  return normalizeLtlPickupWindow(next);
};

// ---------------------------------------------------------------------------
// Dirty-state detection (drives the save-back checkbox enabled/disabled state)
// ---------------------------------------------------------------------------

/**
 * Returns true when the wizard's pickup fields differ from the loaded defaults,
 * i.e. when there is something new to save.
 *
 * Always returns true when `loadedDefaults` is null (never saved → always "dirty").
 */
/**
 * Returns true when the pickup window is complete enough to persist as a default.
 *
 * `willArrangePickupSeparately` satisfies this on its own: it is itself a meaningful
 * default, and `normalizeLtlPickupWindow` forces both times empty whenever it is set,
 * so requiring times would permanently block that configuration.
 */
export const hasCompleteLtlPickupWindow = (formState: FreightBookingFormState): boolean =>
  formState.willArrangePickupSeparately || !!(formState.pickupReadyTime && formState.pickupCloseTime);

export const areLtlDefaultsDirty = (formState: FreightBookingFormState, loadedDefaults: LtlDefaults | null | undefined): boolean => {
  if (!loadedDefaults) return true;

  if (toWizardTime(loadedDefaults.pickupReadyTime) !== formState.pickupReadyTime) return true;
  if (toWizardTime(loadedDefaults.pickupCloseTime) !== formState.pickupCloseTime) return true;
  if (loadedDefaults.willArrangePickupSeparately !== formState.willArrangePickupSeparately) return true;

  const svc = formState.pickupServices;
  const def = loadedDefaults.pickupServices;
  if (
    svc.isInsidePickup !== def.isInsidePickup ||
    svc.isConstructionSitePickup !== def.isConstructionSitePickup ||
    svc.isLiftgatePickup !== def.isLiftgatePickup ||
    svc.isNotifyBeforePickup !== def.isNotifyBeforePickup ||
    svc.isResidentialPickup !== def.isResidentialPickup ||
    svc.isDropAtTerminal !== def.isDropAtTerminal ||
    svc.isGroceryConsolidationPickup !== def.isGroceryConsolidationPickup ||
    svc.isLimitedAccess !== def.isLimitedAccess ||
    svc.isTradeshowPickup !== def.isTradeshowPickup
  )
    return true;

  return false;
};
